/**
 * mapBounds.js
 * ---------------------------------------------------------------
 * Geometry utilities for computing and applying map bounds.
 * All functions are pure (getFeatureBounds) or receive the mapRef
 * explicitly — no class-level state required.
 *
 * Extracted from MapPage.getFeatureBounds / fitToGeo / fitBoundsFromFeatures.
 */
import mapboxgl from "mapbox-gl";

/**
 * Computes [[minLng, minLat], [maxLng, maxLat]] bounds for a single
 * GeoJSON feature. Returns null if the feature has no geometry.
 *
 * @param {object} feature - GeoJSON feature
 * @returns {[[number,number],[number,number]] | null}
 */
export function getFeatureBounds(feature) {
  if (!feature?.geometry) return null;
  const { geometry } = feature;
  const coords = geometry.type === "Polygon"
    ? geometry.coordinates.flat(2)
    : geometry.coordinates.flat(3);
  if (!coords?.length) return null;
  const lons = coords.filter((_, i) => i % 2 === 0);
  const lats = coords.filter((_, i) => i % 2 === 1);
  return [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]];
}

/**
 * Fits the map to cover the full extent of a FeatureCollection.
 *
 * @param {object} geo    - GeoJSON FeatureCollection
 * @param {object} mapRef - React ref to the ReactMap instance
 */
export function fitToGeo(geo, mapRef) {
  if (!mapRef.current || !geo?.features?.length) return;
  const coords = geo.features.flatMap((f) =>
    f.geometry.type === "Polygon"
      ? f.geometry.coordinates.flat(2)
      : f.geometry.coordinates.flat(3)
  );
  if (!coords.length) return;
  const lons = coords.filter((_, i) => i % 2 === 0);
  const lats = coords.filter((_, i) => i % 2 === 1);
  mapRef.current.getMap().fitBounds(
    [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]],
    { padding: 24 }
  );
}

/**
 * Fits the map to cover an array of features and returns the computed bounds.
 *
 * @param {array}  features - GeoJSON feature array
 * @param {object} mapRef   - React ref to the ReactMap instance
 * @returns {mapboxgl.LngLatBounds | null}
 */
export function fitBoundsFromFeatures(features, mapRef) {
  if (!mapRef.current || !features?.length) return null;
  const map = mapRef.current.getMap();
  const bounds = new mapboxgl.LngLatBounds();
  features.forEach((f) => {
    if (!f.geometry) return;
    const coords = f.geometry.type === "Polygon"
      ? f.geometry.coordinates.flat(1)
      : f.geometry.type === "MultiPolygon"
        ? f.geometry.coordinates.flat(2)
        : [];
    coords.forEach(([lng, lat]) => bounds.extend([lng, lat]));
  });
  map.fitBounds(bounds, { padding: 28, duration: 500 });
  return bounds;
}
